import { Injectable } from '@angular/core';
import { IceCandidateDto } from '../../../generated/signalR/ice-candidate-dto';
import { WebRtcSdpOfferRemoteDto } from '../../../generated/signalR/web-rtc-sdp-offer-remote-dto';
import _ from 'lodash';
import { WebRtcService } from '../../../generated/swagger';
import { Store } from '@ngxs/store';
import { AudioService } from '../audio/audio.service';
import { concat, forkJoin, from, Observable, of } from 'rxjs';
import { catchError, distinctUntilChanged, map, mergeMap, switchMap, take, tap } from 'rxjs/operators';
import { WebRtcIceCandidatesAddedByBrowser, WebRtcNegotiationNeeded } from '../../store/server-communication/server-communication.actions';
import { AudioSettingsState } from 'src/app/store/settings/audio-settings/audio-settings.state';
import { WebRtcIceServerRealTimeData } from 'src/generated/signalR/web-rtc-ice-server-real-time-data';

@Injectable({
   providedIn: 'root',
})
export class WebRtcConnectionService {
   private peerConnection: RTCPeerConnection;
   private audio = new Audio();
   private iceCandidateQueue: IceCandidateDto[] = [];

   constructor(private webRtcService: WebRtcService, private store: Store, private audioService: AudioService) {
      this.audioService.chosenAudioOutputDevice$
         .pipe(
            distinctUntilChanged((x, y) => x.deviceId === y.deviceId),
            switchMap((audioDevice) => this.changeOutputDevice$(audioDevice))
         )
         .subscribe();

      this.audioService.chosenAudioInputDevice$
         .pipe(
            distinctUntilChanged((x, y) => x.deviceId === y.deviceId),
            switchMap((inputDevice) =>
               this.store.select(AudioSettingsState.isMicrophoneMuted).pipe(switchMap((isMuted) => this.changeInputDevice$(inputDevice, isMuted)))
            )
         )
         .subscribe();
   }

   public closeConnection() {
      console.log('Closing WebRTC connection');
      this.closeCurrentPeerConnection();
   }

   public startWebRtcClientEndpoint$(iceServers: WebRtcIceServerRealTimeData[]) {
      console.log('Starting WebRTC client endpoint');

      if (!_.isNil(this.peerConnection)) {
         console.log('Another Peer connection is currently available. Will close this instance now.');
         this.peerConnection.close();
      }

      const setupRtcPeerConnection = () => {
         console.log('Setting up RTCPeerConnection');

         const rtcIceServers: RTCIceServer[] = _(iceServers)
            .map((s) => {
               return {
                  urls: [s.uri],
                  username: s.username,
                  credential: s.credential,
               };
            })
            .value();

         const configuration: RTCConfiguration = {
            iceServers: rtcIceServers,
         };

         this.peerConnection = new RTCPeerConnection(configuration);
         this.peerConnection.onicecandidate = (ev) => {
            if (ev === null || ev.candidate === null) {
               return;
            }
            const candidate = {
               sdmMin: ev.candidate.sdpMid,
               mLine: ev.candidate.sdpMLineIndex,
               sdp: ev.candidate.candidate,
            };
            console.log('Client has new ICE candidate available, sending to server', candidate);
            this.store.dispatch(new WebRtcIceCandidatesAddedByBrowser([candidate]));
         };

         this.peerConnection.onconnectionstatechange = (x) => {
            if (_.isNil(this.peerConnection)) {
               console.warn('Client connection state changed, but peerConnection not available!');
               return;
            }
            console.log(`Client connection state changed to: ${this.peerConnection.connectionState}`);
            if (this.peerConnection.connectionState === 'closed' || this.peerConnection.connectionState === 'failed') {
               this.closeConnection();
            } else if (this.peerConnection.connectionState === 'connected') {
               this.peerConnection
                  .getStats(null)
                  .then((stats) => {
                     let selectedCandidatePairId;

                     stats.forEach((report) => {
                        if (report.type === 'transport') {
                           selectedCandidatePairId = report.selectedCandidatePairId;
                        }
                     });

                     if (selectedCandidatePairId) {
                        const anyStats: any = stats; // .get() not known
                        const selectedPair = anyStats.get(selectedCandidatePairId);

                        console.log('Active Candidate Pair:', selectedPair);
                        console.log('Local Candidate:', anyStats.get(selectedPair.localCandidateId));
                        console.log('Remote Candidate:', anyStats.get(selectedPair.remoteCandidateId));
                     } else {
                        console.log('No ICE Candidate Pair selected');
                     }
                  })
                  .catch((error) => console.error('Error fetching stats', error));
            }
         };

         this.peerConnection.onsignalingstatechange = (x) => {
            if (_.isNil(this.peerConnection)) {
               console.warn('Client signaling state changed, but peerConnection not available!');
               return;
            }
            console.log(`Client signaling state changed to: ${this.peerConnection.signalingState}`);
            if (this.peerConnection.signalingState === 'closed') {
               this.closeCurrentPeerConnection();
            }
         };
         this.peerConnection.onnegotiationneeded = (ev) => {
            console.log('Client needs negotiation');
            this.store.dispatch(new WebRtcNegotiationNeeded());
         };
         this.peerConnection.ondatachannel = (ev) => {
            ev.channel.onmessage = (ev1) => {
               console.log('Received data from server', ev1.data);
            };
            ev.channel.onopen = (ev2) => {
               console.log(`Data channel opened`);
            };
            ev.channel.onclose = (ev3) => {
               console.log(`Data channel closed`);
            };
         };
         this.peerConnection.ontrack = (event) => {
            console.log('Handling peer connection onTrack event');
            this.audio.srcObject = event.streams[0];
            this.audio.load();
            this.audio.play();
            console.log('Audio track added to audio element and playback started');
         };
      };

      return this.audioService.chosenAudioInputDevice$.pipe(
         take(1),
         switchMap((inputDevice) => this.getUserMedia$(inputDevice)),
         map((stream) => {
            setupRtcPeerConnection();

            if (_.isNil(this.peerConnection)) {
               console.warn('Not adding track as peerConnection is null');
               return;
            }
            stream.getTracks().forEach((track) => {
               console.log('Adding track to peerConnection', track);
               track.enabled = !this.store.selectSnapshot(AudioSettingsState.isMicrophoneMuted);
               this.peerConnection.addTrack(track, stream);
            });
         })
      );
   }

   public iceCandidatesAdded$(commandIceCandidates: IceCandidateDto[]) {
      if (_.isEmpty(commandIceCandidates)) {
         console.info('No ICE candidates available to add');
         return of(null);
      }
      if (_.isNil(this.peerConnection)) {
         console.error('No PeerConnection available to add IceCandidates');
         return of(null);
      }
      if (_.isNil(this.peerConnection.remoteDescription)) {
         console.warn('No remoteDescription set. Queueing ICE candidates');
         this.iceCandidateQueue = [...this.iceCandidateQueue, ...commandIceCandidates];
         return of(null);
      }

      return of(commandIceCandidates).pipe(
         switchMap((candidates) => {
            const observables = _.map(candidates, (iceCandidate) => {
               console.log('Adding remote ICE candidate', iceCandidate);
               return this.peerConnection
                  .addIceCandidate({
                     sdpMid: iceCandidate.sdmMin,
                     sdpMLineIndex: iceCandidate.mLine,
                     candidate: iceCandidate.sdp,
                  })
                  .then(() => {
                     console.log('Successfully added remote ICE candidate', iceCandidate);
                  })
                  .catch((error) => {
                     console.error('Failed to add remote ICE candidate', iceCandidate, error);
                  });
            });
            return forkJoin(observables);
         })
      );
   }

   public negotiationNeeded$() {
      console.log('Negotiation needed, creating local SDP offer');
      // todo: Once we have TS definitions updated, we can remove the cast

      return from((this.peerConnection as any).setLocalDescription()).pipe(
         switchMap(() =>
            this.webRtcService
               .signalingSdp({
                  sdpOffer: {
                     sdp: this.peerConnection.localDescription.sdp,
                     type: this.peerConnection.localDescription.type,
                  },
               })
               .pipe(tap(() => console.log('Local SDP offer sent to server')))
         ),
         catchError((e) => {
            console.error('Failed to create and send local SDP offer', e);
            return of(null);
         })
      );
   }

   public sdpReceivedFromServer$(remoteSdp: WebRtcSdpOfferRemoteDto) {
      if (_.isNil(this.peerConnection)) {
         console.warn('No PeerConnection available to add SDP');
         return of(null);
      }
      console.log(`Processing remote ${remoteSdp.type} SDP`);

      const setRemoteSdp$ = from(
         this.peerConnection
            .setRemoteDescription({
               type: remoteSdp.type as RTCSdpType,
               sdp: remoteSdp.sdp,
            })
            .then(() => {
               console.log(`Remote ${remoteSdp.type} SDP set successfully`);
            })
            .catch((error) => {
               console.error(`Failed to set remote ${remoteSdp.type} SDP`, error);
            })
      );

      const processCachedIceCandidates$ = of(null).pipe(
         tap(() => console.log('Start adding cached ICE candidates')),
         switchMap(() => this.iceCandidatesAdded$(this.iceCandidateQueue)),
         tap(() => console.log('Finished adding cached ICE candidates')),
         tap(() => (this.iceCandidateQueue = []))
      );

      if (remoteSdp.type === 'answer') {
         return concat(setRemoteSdp$, processCachedIceCandidates$);
      }

      const createAnswer$ = of(null).pipe(
         tap(() => console.log('Creating answer to offered SDP')),
         // todo: Once we have TS definitions updated, we can remove the cast
         switchMap(() => (this.peerConnection as any).setLocalDescription()),
         switchMap(() =>
            this.webRtcService
               .signalingSdp({
                  sdpOffer: {
                     sdp: this.peerConnection.localDescription.sdp,
                     type: this.peerConnection.localDescription.type,
                  },
               })
               .pipe(tap(() => console.log('Answer SDP sent to server')))
         ),
         catchError((e) => {
            console.error('Failed to create and send answer SDP', e);
            return of(null);
         })
      );

      // offer
      return concat(setRemoteSdp$, createAnswer$, processCachedIceCandidates$);
   }

   private getUserMedia$(inputDevice: MediaDeviceInfo): Observable<MediaStream> {
      const deviceConstraints: MediaStreamConstraints = {
         audio: {
            deviceId: { exact: inputDevice.deviceId },
         },
         video: false,
      };

      console.log('Calling getUserMedia() with constraints', deviceConstraints);

      return from(navigator.mediaDevices.getUserMedia(deviceConstraints)).pipe(
         tap((stream) => console.log('Received media stream', stream)),
         catchError((error) => {
            console.error('Failed to get user media', error);

            return of(null);
         })
      );
   }

   private changeOutputDevice$(device: MediaDeviceInfo) {
      console.log('Audio output device changed to', device);
      // browsers might not support setting the sink id
      if (typeof this.audio.sinkId === 'undefined') {
         console.warn('Setting audio output device is not supported by this browser');

         return of(null);
      }

      return from(this.audio.setSinkId(device.deviceId)).pipe(
         tap(() => console.log('Audio output device set successfully')),
         catchError((error) => {
            console.error('Failed to set audio output device', error);

            return of(null);
         })
      );
   }

   private changeInputDevice$(inputDevice: MediaDeviceInfo, isMuted: boolean) {
      console.log('Audio input device changed to', inputDevice);

      if (!this.peerConnection) {
         console.warn('PeerConnection is not available to change input device');

         return of(null);
      }

      // change input device on the fly for active connections
      return this.getUserMedia$(inputDevice).pipe(
         mergeMap((mediaStream) => {
            const replacements = _.flatMap(mediaStream.getAudioTracks(), (audioTrack) => {
               audioTrack.enabled = !isMuted;

               return this.peerConnection.getSenders().map((sender: RTCRtpSender) => {
                  if (sender.track?.kind === audioTrack.kind) {
                     console.log('Replacing track in peerConnection', audioTrack);

                     return from(sender.replaceTrack(audioTrack)).pipe(
                        tap(() => console.log('Track replaced successfully')),
                        catchError((error) => {
                           console.error('Failed to replace track', error);

                           return of(null);
                        })
                     );
                  }

                  return of(null);
               });
            });

            return forkJoin(replacements);
         })
      );
   }

   private closeCurrentPeerConnection() {
      if (_.isNil(this.peerConnection)) {
         console.warn('No peer connection available to close.');
      } else {
         console.log('Closing current peer connection');
         this.peerConnection.getSenders().forEach((sender) => sender.track?.stop());
         this.peerConnection.close();
         this.peerConnection = null;
         console.log('Peer connection closed');
      }
   }
}
